import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {getCommaSeparatedList} from 'nutshell-core/get-comma-separated-list';

import {colors} from 'shells/colors/colors';
import {Link} from 'shells/link/link';
import {DisplayOnlyTable} from 'shells/table/display-only-table';
import type {SpecialState} from 'shells/table/types';
import {shouldShowDelimiter} from 'shells/utils/should-show-delimiter';

import type {MeetingStubFragment} from 'nutshell-graphql-types';

import {getStatusBadge, getNeedsAttentionStatusBadge, getMeetingTypeAsString} from './helpers';

type Props = {
    isLoading: boolean;
    isFirstLoad: boolean;
    meetings: MeetingStubFragment[] | null | undefined;
    error: string | null | undefined;
    specialStates?: SpecialState[];
};

export function MeetingsTable(props: Props) {
    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (meeting: MeetingStubFragment) => meeting,
                dataType: 'linkedName',
                hasLeftBorder: true,
                shouldTruncate: true,
                titleKey: 'name',
                width: 200,
            },
            {
                id: 'status',
                Header: 'Status',
                accessor: (meeting: MeetingStubFragment) => getStatusBadge(meeting),
                dataType: 'statusBadge',
                minWidth: 170,
            },
            {
                id: 'needs-attention',
                Header: 'Needs attention',
                accessor: (meeting: MeetingStubFragment) => getNeedsAttentionStatusBadge(meeting),
                dataType: 'statusBadge',
                minWidth: 130,
            },
            {
                id: 'creator',
                Header: 'Host(s)',
                accessor: (meeting: MeetingStubFragment) => meeting.creator,
                dataType: 'component',
                width: 150,
                // @ts-expect-error
                sortType: (rowA, rowB) => {
                    // TODO UPDATE
                    return rowA.original.creator.name >= rowB.original.creator.name ? 1 : -1;
                },
                Cell: ({cell}: any) => {
                    if (cell.row.original.meetingType === 'ROUND_ROBIN' && cell.row.original.team) {
                        return (
                            <div style={{color: colors.textUsers}}>
                                {cell.row.original.team.name}
                            </div>
                        );
                    }

                    const allHosts = [cell.value, ...cell.row.original.cohosts];

                    return (
                        <div
                            className='truncate'
                            title={getCommaSeparatedList(allHosts.map((cohost) => cohost.name))}
                        >
                            {allHosts.map((cohost, index) => (
                                <span key={cohost.id}>
                                    <span style={{color: colors.textUsers}}>{cohost.name}</span>
                                    {shouldShowDelimiter(index, allHosts.length) ? (
                                        <>,&nbsp;</>
                                    ) : undefined}
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                id: 'meetingType',
                Header: 'Meeting type',
                accessor: (meeting: MeetingStubFragment) => getMeetingTypeAsString(meeting),
                dataType: 'string',
                width: 110,
            },
            {
                id: 'activityType',
                Header: 'Activity type',
                accessor: (meeting: MeetingStubFragment) => meeting.activityType.name,
                dataType: 'string',
                width: 110,
            },
            {
                id: 'durationMinutes',
                Header: 'Duration',
                accessor: (meeting: MeetingStubFragment) => meeting.durationMinutes,
                dataType: 'duration',
            },
            {
                id: 'bookings',
                Header: '# Bookings',
                accessor: (meeting: MeetingStubFragment) => meeting.bookings.length,
                dataType: 'integer',
                width: 90,
                Cell: ({cell}: any) => {
                    return (
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            size='small'
                            variant='primary'
                            to={`${cell.row.original.htmlUrlPath}/bookings`}
                        >
                            {cell.value.toLocaleString()}
                        </Link>
                    );
                },
            },
            {
                id: 'numViews',
                Header: 'Page visits',
                accessor: (meeting: MeetingStubFragment) => meeting.numViews,
                dataType: 'integer',
                width: 90,
                Cell: ({cell}: any) => cell.value.toLocaleString(),
            },
            {
                id: 'bookingRate',
                Header: 'Conversion rate',
                accessor: (meeting: MeetingStubFragment) => meeting.bookingRate,
                dataType: 'percentage',
                width: 110,
                minWidth: 110, // override default minWidth
                maxWidth: 110, // override default maxWidth
                hasRightBorder: true,
            },
        ],
        []
    );

    return (
        <DisplayOnlyTable
            // @ts-expect-error
            columns={columns}
            data={props.meetings || []}
            isLoading={props.isLoading}
            isFirstLoad={props.isFirstLoad}
            error={props.error}
            fullWidth={true}
            disableSorting={false}
            specialStates={props.specialStates}
            isResizable={true}
        />
    );
}
